<template>
  <div>
    <HeroHeader
      eyebrow="RECAUDACIÓN"
      title="Letras pendientes"
      :counter-num="bills.length"
      counter-unit="sin pagar"
      :back="true"
      @back="$router.push({name: 'collection-dashboard'})"
    >
      <template #toolbar>
        <button class="btn btn--outline" @click="loadData">Actualizar</button>
      </template>
    </HeroHeader>

    <ChipsBar :chips="statusChips" v-model="filterStatus"/>

    <div class="filter-bar filter-bar--no-top">
      <SearchInput v-model="search" placeholder="Buscar inquilino o documento…"/>
      <select v-model="filterEstate">
        <option value="All">Todas las propiedades</option>
        <option v-for="e in estates" :key="e.id" :value="e.id">{{ e.description }}</option>
      </select>
      <SortSelect :options="sortOptions" v-model="sortBy" />
    </div>

    <div class="dt-wrapper">
      <table class="dt" v-if="pagedBills.length > 0">
        <thead>
          <tr>
            <th>Inquilino</th>
            <th>Propiedad</th>
            <th>Monto</th>
            <th>Vencimiento</th>
            <th v-if="showingAlerts">Estado</th>
            <th v-else>Días</th>
            <th>Comprobante</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="bill in pagedBills" :key="bill.id" :class="isOverdue(bill) ? 'dt-row--overdue' : ''">
            <td>
              <div class="dt-cell">
                <span class="dt-cell__primary">{{ bill.client.name || bill.client.alias }}</span>
                <span class="dt-cell__secondary">{{ bill.client.document }}</span>
              </div>
            </td>
            <td>{{ bill.client.local ? bill.client.local.description : '—' }}</td>
            <td><span class="dt-cell__primary">{{ formatAmount(bill.total, bill.client.currency) }}</span></td>
            <td>{{ formatDate(bill.deadline) }}</td>
            <td v-if="showingAlerts">
              <span class="badge" :class="bill.paid ? 'badge--success' : 'badge--error'">
                {{ bill.paid ? 'Pagada' : 'Pendiente' }}
              </span>
            </td>
            <td v-else>
              <span class="badge" :class="daysVariant(bill.deadline)">
                {{ daysLabel(bill.deadline) }}
              </span>
            </td>
            <td>
              <div v-if="voucherCount(bill)" class="voucher-cell">
                <VoucherStatus :audit="bill.voucherAudit" />
                <span class="voucher-cell__meta">
                  {{ voucherCount(bill) }} {{ voucherCount(bill) === 1 ? 'comprobante' : 'comprobantes' }}
                </span>
              </div>
              <span v-else class="dt-cell__secondary">—</span>
            </td>
            <td>
              <div class="dt-actions">
                <button class="btn btn--sm" @click="openPay(bill)">
                  {{ bill.paid ? 'Ver' : 'Registrar pago' }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <EmptyState
        v-else
        title="¡Todo pagado!"
        message="No hay letras pendientes de pago."
      >
        <template #icon>
          <svg class="empty-state__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </template>
      </EmptyState>
    </div>

    <div v-if="filteredBills.length > perPage" class="dt-pagination">
      <span class="dt-pagination__info">{{ pageFrom }}–{{ pageTo }} de {{ filteredBills.length }}</span>
      <div class="dt-pagination__controls">
        <button :disabled="page <= 1" @click="page--">← Anterior</button>
        <button :disabled="page >= totalPages" @click="page++">Siguiente →</button>
      </div>
    </div>

    <Pay
      :data="sidebarData"
      :show-navbar="showPaySidebar"
      :class="{'show': showPaySidebar}"
      @close="showPaySidebar = false"
      @audit="onAuditUpdate"
      @update="onPayUpdate"
      @added="onBillAdded"
    />
  </div>
</template>

<script>
import { db } from '@/firebase/firebaseConfig'
import Pay from '../bills/Pay'
import HeroHeader from '@/components/HeroHeader'
import ChipsBar from '@/components/ChipsBar'
import EmptyState from '@/components/EmptyState'
import SortSelect from '@/components/SortSelect'
import VoucherStatus from '@/components/VoucherStatus'
import SearchInput from '@/components/SearchInput'
import { sortItems } from '@/utils/sort'
import moment from 'moment'

export default {
  name: 'PendingBills',
  components: { Pay, HeroHeader, ChipsBar, EmptyState, SortSelect, SearchInput, VoucherStatus },
  data () {
    return {
      bills: [],
      estates: [],
      search: '',
      filterEstate: 'All',
      filterStatus: 'all',
      sortBy: 'deadline_asc',
      sortOptions: [
        { value: 'deadline_asc', label: 'Vencimiento (próximo primero)', field: 'deadline', type: 'date', dir: 'asc' },
        { value: 'deadline_desc', label: 'Vencimiento (lejano primero)', field: 'deadline', type: 'date', dir: 'desc' },
        { value: 'amount_desc', label: 'Monto (mayor a menor)', field: 'total', type: 'number', dir: 'desc' },
        { value: 'tenant', label: 'Inquilino (A–Z)', field: 'client.name', type: 'string', dir: 'asc' },
        { value: 'estate', label: 'Propiedad (A–Z)', field: 'client.local.description', type: 'string', dir: 'asc' }
      ],
      page: 1,
      perPage: 20,
      showPaySidebar: false,
      sidebarData: { client: { currency: { code: '' } } },
      alertBills: [],
      alertsLoaded: false
    }
  },
  computed: {
    statusChips () {
      const now = new Date()
      const overdue = this.bills.filter(b => b.deadline < now).length
      const upcoming = this.bills.filter(b => b.deadline >= now).length
      return [
        { label: 'Todos', value: 'all' },
        { label: 'Vencidos', value: 'overdue', count: overdue || null },
        { label: 'Por vencer', value: 'upcoming', count: upcoming || null },
        // No es un filtro de esta lista: es OTRO universo. Una alerta puede caer
        // sobre una letra ya pagada, y ésas no están en "pendientes" — sin este
        // chip no habría dónde encontrarlas salvo sabiendo de antemano de quién son.
        { label: 'Con alerta', value: 'alerts', count: this.alertBills.length || null }
      ]
    },
    showingAlerts () { return this.filterStatus === 'alerts' },
    filteredBills () {
      const now = new Date()
      let list = this.showingAlerts ? this.alertBills : this.bills

      if (this.filterStatus === 'overdue') list = list.filter(b => b.deadline < now)
      else if (this.filterStatus === 'upcoming') list = list.filter(b => b.deadline >= now)

      if (this.filterEstate !== 'All') {
        list = list.filter(b => b.client.local && b.client.local.id === this.filterEstate)
      }
      if (this.search.trim()) {
        const q = this.search.trim().toLowerCase()
        list = list.filter(b =>
          (b.client.name || '').toLowerCase().includes(q) ||
          (b.client.alias || '').toLowerCase().includes(q) ||
          (b.client.document || '').toLowerCase().includes(q)
        )
      }
      return list
    },
    sortedBills () {
      return sortItems(this.filteredBills, this.sortOptions.find(o => o.value === this.sortBy))
    },
    totalPages () { return Math.ceil(this.filteredBills.length / this.perPage) },
    pageFrom () { return (this.page - 1) * this.perPage + 1 },
    pageTo () { return Math.min(this.page * this.perPage, this.filteredBills.length) },
    pagedBills () {
      return this.sortedBills.slice((this.page - 1) * this.perPage, this.page * this.perPage)
    }
  },
  watch: {
    search () { this.page = 1 },
    filterEstate () { this.page = 1 },
    async filterStatus (value) {
      this.page = 1
      // Se paga la consulta sólo cuando alguien pide ver las alertas.
      if (value === 'alerts' && !this.alertsLoaded) await this.getAlertBills()
    },
    sortBy () { this.page = 1 }
  },
  async created () { await this.loadData() },
  methods: {
    async loadData () {
      try {
        this.$store.dispatch('showProgress')
        this.bills = []
        this.estates = []
        await this.getEstates()
        await this.getPendingBills()
      } catch (e) {
        this.$toast.error('Error al cargar datos')
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },
    async getEstates () {
      const snap = await db.collection('estates').get()
      snap.forEach(doc => this.estates.push({ id: doc.id, description: doc.data().description }))
    },
    async getPendingBills () {
      const snap = await db.collectionGroup('bills')
        .where('paid', '==', false)
        .orderBy('deadline', 'asc').get()
      snap.forEach(doc => {
        const obj = { id: doc.id, ...doc.data() }
        obj.deadline = doc.data().deadline.toDate()
        obj.paymentDate = doc.data().paymentDate ? doc.data().paymentDate.toDate() : null
        this.bills.push(obj)
      })
    },
    async getAlertBills () {
      try {
        this.$store.dispatch('showProgress')
        this.alertBills = []
        const snap = await db.collectionGroup('bills')
          .where('voucherAlert', '==', true)
          .orderBy('deadline', 'desc').get()
        snap.forEach(doc => {
          const obj = { id: doc.id, ...doc.data() }
          obj.deadline = doc.data().deadline.toDate()
          obj.paymentDate = doc.data().paymentDate ? doc.data().paymentDate.toDate() : null
          this.alertBills.push(obj)
        })
        this.alertsLoaded = true
      } catch (e) {
        this.$toast.error('Error al cargar las alertas de comprobante')
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },
    voucherCount (bill) {
      const all = [
        ...(bill.vouchers || []),
        ...(bill.clientVouchers || []),
        ...(bill.voucher ? [bill.voucher] : [])
      ].filter(Boolean)
      return new Set(all).size
    },
    isOverdue (bill) { return bill.deadline < new Date() },
    daysVariant (deadline) {
      const days = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24))
      if (days <= 0) return 'badge--error'
      if (days <= 7) return 'badge--warning'
      return ''
    },
    daysLabel (deadline) {
      const days = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24))
      return days > 0 ? 'en ' + days + 'd' : Math.abs(days) + 'd vencido'
    },
    formatDate (date) { return moment(date).locale('es').format('DD/MMM/YY') },
    formatAmount (total, currency) {
      const code = currency && currency.code ? currency.code : 'PEN'
      return Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(total)
    },
    openPay (bill) {
      this.sidebarData = { ...bill }
      this.showPaySidebar = true
    },
    onAuditUpdate (patch) {
      // Refresca el veredicto en la lista visible, sin quitar la fila ni volver
      // a consultar la colección entera.
      for (const list of [this.bills, this.alertBills]) {
        const idx = list.findIndex(b => b.id === patch.id)
        if (idx !== -1) Object.assign(list[idx], patch)
      }
    },
    onPayUpdate (updatedBill) {
      const idx = this.bills.findIndex(b => b.id === updatedBill.id)
      if (idx !== -1) this.bills.splice(idx, 1)
      this.showPaySidebar = false
    },
    onBillAdded (newBill) {
      // Nueva letra por diferencia: agregar al listado si está sin pagar
      if (!newBill.paid) this.bills.push(newBill)
      this.showPaySidebar = false
    }
  }
}
</script>
